import { Box, Button } from '@mui/material'
import { formatFullDate } from 'utils/util'
import { Page } from 'components/Shared/Page'
import { DataTable } from 'components/Shared/DataTable'
import { PageHeader } from 'components/Shared/PageHeader'
import { useGetSweepstakesByTypeWithGrouping } from 'hooks/api/useGetSweepstakesByTypeWithGrouping'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { type GridColDef, type GridEventListener } from '@mui/x-data-grid'
import { type Sweepstake } from 'types/api'
import { SweepstakesStatusLabel } from 'components/Sweepstakes/SweepstakesStatusLabel'
import { GridSearchField } from 'components/GridSearchField/GridSearchField'
import { useState } from 'react'
import { DeleteLocationSweepstakesRenderCell } from './DeleteLocationSweepstakesRenderCell'
import { ErrorIndicator } from 'components/ErrorIndicator/ErrorIndicator'
import { NoDataIndicator } from 'components/NoDataIndicator/NoDataIndicator'

const sweepstakeType = (sweepstake: Sweepstake) => {
  if (sweepstake?.corporateAccountId) {
    return sweepstake?.corporateAccount?.name
  } else if (sweepstake?.organizationId) {
    return sweepstake?.organization?.name
  } else if (sweepstake?.licensedEstablishments?.length === 1) {
    return sweepstake?.licensedEstablishments?.[0]?.standardName
  } else {
    return `${sweepstake?.licensedEstablishments?.length} Locations`
  }
}

const columns: Array<GridColDef<Sweepstake>> = [
  {
    field: 'name',
    headerName: 'Sweepstakes Name',
    minWidth: 360,
    flex: 1,
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 230,
    flex: 0.25,
    renderCell: (params) => {
      const status = params.row.sweepstakeData?.state ?? 'Draft'
      return <SweepstakesStatusLabel status={status} />
    },
  },
  {
    field: 'startDate',
    headerName: 'Start',
    minWidth: 150,
    flex: 0.25,
    renderCell: (params) => <Box>{formatFullDate(params.value)}</Box>,
  },
  {
    field: 'endDate',
    headerName: 'End',
    minWidth: 150,
    flex: 0.25,
    renderCell: (params) => <Box>{formatFullDate(params.value)}</Box>,
  },
  {
    field: 'type',
    headerName: 'Type',
    minWidth: 150,
    flex: 0.25,
    renderCell: (params) => <Box>{sweepstakeType(params.row)}</Box>,
  },
  {
    field: '__menu__',
    headerName: '',
    align: 'right',

    renderCell: (params) => {
      // Only display the option menu on the row if the status column is 'draft'
      const status = params.row.sweepstakeData?.state ?? 'Draft'
      return status === 'Draft' ? (
        <DeleteLocationSweepstakesRenderCell params={params} />
      ) : null
    },
  },
]

export const LocationSweepstakesPage = () => {
  const navigate = useNavigate()
  const query = useGetSweepstakesByTypeWithGrouping({
    type: 'LocationSweepstake',
  })
  const [filteredData, setFilteredData] = useState<Sweepstake[]>()

  const handleFilteredData = (filteredData: Sweepstake[]) => {
    setFilteredData(filteredData)
  }

  const selectCell: GridEventListener<'cellClick'> = (params) => {
    if (params.field !== '__menu__' && params.id !== null) {
      navigate(`/Contests/LocationSweepstakes/${params.id}/Overview`, {
        state: { sweepstake: params.row },
      })
    }
  }

  return (
    <Page
      header={
        <PageHeader
          title="Location Sweepstakes"
          eyebrow="CONTESTS"
          actionButton={
            <RouterLink
              to="/Contests/LocationSweepstakes/New"
              className="gap-1"
            >
              <Button
                variant="contained"
                sx={{
                  px: '16px',
                  width: { xs: '100%', sm: '100%', md: 200, lg: 200 },
                }}
              >
                Create New Location Sweepstake
              </Button>
            </RouterLink>
          }
        />
      }
    >
      {query.isPending || query.isFetching ? (
        <ActivityIndicator />
      ) : query.isError ? (
        <ErrorIndicator />
      ) : !query.data ? (
        <NoDataIndicator />
      ) : (
        <>
          <GridSearchField<'FullContestDTO'>
            placeholder="Search Location Sweepstakes"
            data={query.data ?? []}
            handleFilteredData={handleFilteredData}
            fullWidth
          />
          <DataTable
            columns={columns}
            rows={filteredData ?? query.data}
            onCellClick={selectCell}
          />
        </>
      )}
    </Page>
  )
}
